import { screenSizeService } from "./ScreenSizeService.js";
import { AsyncDirective, directive } from "lit/async-directive.js";
//#region src/screen-size/responsive-directives.ts
/**
* Responsive Directives - Lit directives for responsive values in templates
*
* These directives allow setting different values based on the current breakpoint.
* They automatically update when the screen size changes.
*
* @example
* ```typescript
* import { html } from 'lit';
* import { responsiveText, responsiveValue } from '@progressive-development/pd-spa-helper';
*
* // Text that changes based on screen size
* // sm: applies to sm and xs (smaller screens)
* // default: applies to md, lg, xl (larger screens)
* html`<pd-button text="${responsiveText({
*   default: 'Hier geht es zur perfekten Party',
*   sm: 'Party starten'
* })}"></pd-button>`
*
* // Generic values (numbers, booleans, etc.)
* // md: applies to md, sm, xs
* // default: applies to lg, xl
* html`<my-grid columns="${responsiveValue({
*   default: 4,
*   md: 2
* })}"></my-grid>`
* ```
*/
/**
* Resolves the value for the current breakpoint with fallback logic.
*
* Semantics: A breakpoint key (e.g., `sm`) applies to that breakpoint AND ALL SMALLER ones.
* - `sm: 'short'` means: for sm and xs, use 'short'
* - `md: 'medium'` means: for md, sm, and xs, use 'medium'
* - `default` is used for breakpoints larger than any defined key
*
* Algorithm: Search upward from current breakpoint to find the smallest defined key >= current.
*/
function resolveResponsiveValue(options, currentBreakpoint) {
	const breakpointOrder = screenSizeService.breakpointOrder;
	const currentIndex = breakpointOrder.indexOf(currentBreakpoint);
	for (let i = currentIndex; i < breakpointOrder.length; i++) {
		const value = options[breakpointOrder[i]];
		if (value !== void 0) return value;
	}
	return options.default;
}
/**
* ResponsiveTextDirective - Returns different text based on screen size
*/
var ResponsiveTextDirective = class extends AsyncDirective {
	render(options) {
		this._options = options;
		if (!this._unsubscribe) this._unsubscribe = screenSizeService.subscribe(() => {
			this.setValue(this._getText());
		});
		return this._getText();
	}
	disconnected() {
		this._unsubscribe?.();
		this._unsubscribe = void 0;
	}
	reconnected() {
		if (!this._unsubscribe) this._unsubscribe = screenSizeService.subscribe(() => {
			this.setValue(this._getText());
		});
	}
	_getText() {
		if (!this._options) return "";
		return resolveResponsiveValue(this._options, screenSizeService.breakpoint);
	}
};
/**
* ResponsiveValueDirective - Returns different values based on screen size (generic)
*/
var ResponsiveValueDirective = class extends AsyncDirective {
	render(options) {
		this._options = options;
		if (!this._unsubscribe) this._unsubscribe = screenSizeService.subscribe(() => {
			this.setValue(this._getValue());
		});
		return this._getValue();
	}
	disconnected() {
		this._unsubscribe?.();
		this._unsubscribe = void 0;
	}
	reconnected() {
		if (!this._unsubscribe) this._unsubscribe = screenSizeService.subscribe(() => {
			this.setValue(this._getValue());
		});
	}
	_getValue() {
		if (!this._options) return void 0;
		return resolveResponsiveValue(this._options, screenSizeService.breakpoint);
	}
};
/**
* Directive for responsive text values.
* Returns different text based on the current screen size breakpoint.
*
* @param options - Object with default and optional breakpoint-specific texts
* @returns The text for the current breakpoint (with fallback to smaller breakpoints)
*
* @example
* ```typescript
* html`<pd-button text="${responsiveText({
*   default: 'Click here to continue',
*   sm: 'Continue',
*   xs: 'Go'
* })}"></pd-button>`
* ```
*/
var responsiveText = directive(ResponsiveTextDirective);
/**
* Directive for responsive generic values.
* Returns different values based on the current screen size breakpoint.
*
* @param options - Object with default and optional breakpoint-specific values
* @returns The value for the current breakpoint (with fallback to smaller breakpoints)
*
* @example
* ```typescript
* html`<my-component
*   columns="${responsiveValue({ default: 4, md: 2, sm: 1 })}"
*   .showSidebar="${responsiveValue({ default: true, sm: false })}"
* ></my-component>`
* ```
*/
var responsiveValue = directive(ResponsiveValueDirective);
//#endregion
export { responsiveText, responsiveValue };
